import React, { useState } from 'react';
import { backendApi } from '../api/backendApi';
import { useQuery } from '@tanstack/react-query';
import { Plus, FileText, UserPlus, LayoutDashboard } from 'lucide-react';
import { Button } from '../Components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../Components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Components/ui/select';
import MetricCard from '../Components/dashboard/MetricCard';
import ActivityList from '../Components/dashboard/ActivityList';
import TargetWidget from '../Components/dashboard/TargetWidget';

import { colors as DS, TechLabel, StatusBadge } from '../../design-system';

const ACCENT = DS.product.ats;

export default function Dashboard() {
  const [timeFilter, setTimeFilter] = useState('today');

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => backendApi.entities.Job.list('-created_date', 100),
    initialData: [],
  });

  const { data: applicants = [] } = useQuery({
    queryKey: ['applicants'],
    queryFn: () => backendApi.entities.Applicant.list('-created_date', 100),
    initialData: [],
  });

  const { data: submissions = [] } = useQuery({
    queryKey: ['submissions'],
    queryFn: () => backendApi.entities.Submission.list('-created_date', 100),
    initialData: [],
  });

  const { data: interviews = [] } = useQuery({
    queryKey: ['interviews'],
    queryFn: () => backendApi.entities.Interview.list('-created_date', 100),
    initialData: [],
  });

  const { data: placements = [] } = useQuery({
    queryKey: ['placements'],
    queryFn: () => backendApi.entities.Placement.list('-created_date', 100),
    initialData: [],
  });

  const { data: benchCandidates = [] } = useQuery({
    queryKey: ['benchCandidates'],
    queryFn: () => backendApi.entities.BenchCandidate.list('-created_date', 100),
    initialData: [],
  });

  const { data: targets = [] } = useQuery({
    queryKey: ['targets'],
    queryFn: () => backendApi.entities.Target.list('-created_date', 10),
    initialData: [],
  });

  const activeJobs = jobs.filter((j) => j.status === 'Active').length;
  const internalSubmissions = submissions.filter((s) => s.submission_type === 'Internal').length;
  const clientSubmissions = submissions.filter((s) => s.submission_type === 'Client').length;
  const scheduledInterviews = interviews.filter((i) => i.status === 'Scheduled').length;
  const awaitingFeedback = interviews.filter((i) => i.status === 'Completed' && !i.feedback).length;
  const noShow = interviews.filter((i) => i.status === 'No Show').length;

  const recruitmentActivity = [
    { label: 'Applicants',          value: applicants.length, color: 'blue' },
    { label: 'Active Jobs',         value: activeJobs,         color: 'red'  },
    { label: 'Submissions',         value: submissions.length, color: 'blue' },
    { label: 'Client Submissions',  value: clientSubmissions,  color: 'blue' },
    { label: 'No Show',             value: noShow,             color: 'blue' },
    { label: 'Interviews',          value: scheduledInterviews,color: 'blue' },
    { label: 'Awaiting Feedback',   value: awaitingFeedback,   color: 'blue' },
    { label: 'Placements',          value: placements.length,  color: 'red'  },
  ];

  const benchActivity = [
    { label: 'Active Bench',           value: benchCandidates.filter((b) => b.bench_status === 'Available').length, color: 'blue' },
    { label: 'Vendor Submissions',     value: 0,                                                                     color: 'blue' },
    { label: 'Requirement Submissions',value: 0,                                                                     color: 'red'  },
    { label: 'Interviews',             value: scheduledInterviews,                                                   color: 'blue' },
    { label: 'No Show',                value: noShow,                                                                color: 'blue' },
    { label: 'Assigned Bench',         value: benchCandidates.filter((b) => b.bench_status === 'Assigned').length,   color: 'red'  },
    { label: 'Jobboard Submissions',   value: 0,                                                                     color: 'blue' },
  ];

  return (
    <div
      className="max-w-full mx-auto space-y-6 overflow-x-hidden"
      style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: DS.forest }}
    >
      {/* HEADER */}
      <div
        className="relative p-6 md:p-7"
        style={{ background: DS.paper, border: `1px solid ${DS.hairline}`, borderLeft: `4px solid ${ACCENT}` }}
      >
        <div className="flex items-start justify-between mb-1 flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 flex items-center justify-center flex-shrink-0"
              style={{ background: DS.forest, color: DS.paper }}
            >
              <LayoutDashboard className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div>
              <StatusBadge color={ACCENT}>Dashboard · ats core</StatusBadge>
              <h1 className="mt-2 text-2xl md:text-3xl font-extrabold leading-tight tracking-tight" style={{ color: DS.forest }}>
                Recruitment overview
              </h1>
              <p className="mt-1 text-sm" style={{ color: DS.grid }}>
                Live view of every active pipeline.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="ds-skeu-btn-primary cursor-pointer h-10 px-4 text-xs font-bold uppercase inline-flex items-center"
              style={{ letterSpacing: "0.16em" }}
            >
              <Plus className="w-3.5 h-3.5 mr-2" />
              Create job
            </button>
            <Button
              variant="outline"
              className="rounded-none h-10 text-xs font-bold uppercase border bg-transparent shadow-none transition-colors hover:bg-[#1A3C2B]/[0.04]"
              style={{ letterSpacing: "0.16em", borderColor: DS.forest, color: DS.forest }}
            >
              <FileText className="w-3.5 h-3.5 mr-2" />
              Job template
            </Button>
            <Button
              variant="outline"
              className="rounded-none h-10 text-xs font-bold uppercase border bg-transparent shadow-none transition-colors hover:bg-[#1A3C2B]/[0.04]"
              style={{ letterSpacing: "0.16em", borderColor: DS.forest, color: DS.forest }}
            >
              <UserPlus className="w-3.5 h-3.5 mr-2" />
              New applicant
            </Button>
          </div>
        </div>
      </div>

      {/* RECRUITMENT SUMMARY */}
      <div style={{ background: DS.paper, border: `1px solid ${DS.hairline}` }}>
        <div className="flex items-center justify-between px-6 py-4 border-b flex-wrap gap-3" style={{ borderColor: DS.hairline }}>
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 animate-pulse" style={{ background: ACCENT }} />
            <div>
              <TechLabel style={{ color: ACCENT }}>02 · summary</TechLabel>
              <h2 className="text-base font-bold leading-tight mt-0.5" style={{ color: DS.forest }}>
                My recruitment summary
              </h2>
            </div>
          </div>
          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger
              className="w-32 h-9 rounded-none text-xs font-bold uppercase"
              style={{ letterSpacing: "0.16em", borderColor: DS.hairline, color: DS.forest }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-4">
            <MetricCard label="Active Jobs"          value={activeJobs}                            color="blue" />
            <MetricCard label="Assigned On"          value={jobs.filter((j) => j.assigned_to).length} color="gray" />
            <MetricCard label="Internal Submissions" value={internalSubmissions}                   color="gray" />
            <MetricCard label="Submitted to Client"  value={clientSubmissions}                     color="gray" />
            <MetricCard label="Job Age"              value="0"                                     color="gray" />
            <MetricCard label="Awaiting Feedback"    value={awaitingFeedback}                      color="gray" />
            <MetricCard label="Interviews"           value={scheduledInterviews}                   color="gray" />
            <MetricCard label="Placements"           value={placements.length}                     color="gray" />
          </div>
          <div className="mt-6">
            <TechLabel style={{ color: `${DS.grid}99` }}>showing 0 of 0 entries</TechLabel>
          </div>
        </div>
      </div>

      {/* ROW: Tabs + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2" style={{ background: DS.paper, border: `1px solid ${DS.hairline}` }}>
          <Tabs defaultValue="interviews" className="w-full">
            <div className="border-b px-6 pt-5" style={{ borderColor: DS.hairline }}>
              <TabsList
                className="bg-transparent rounded-none p-0 h-auto gap-px"
                style={{ background: DS.hairline }}
              >
                {["interviews", "jobs", "tasks"].map((v) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-[#1A3C2B] data-[state=active]:text-[#F7F7F5] data-[state=active]:shadow-none"
                    style={{ letterSpacing: "0.16em", background: DS.paper, color: DS.grid }}
                  >
                    {v === "interviews" ? "Interviews" : v === "jobs" ? "Jobs starting" : "Tasks"}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="mt-3 mb-4">
                <TechLabel style={{ color: `${DS.grid}99` }}>
                  data · next 7 days
                </TechLabel>
              </div>
            </div>
            {["interviews", "jobs", "tasks"].map((v) => (
              <TabsContent key={v} value={v} className="p-6">
                <div className="text-center py-12">
                  <TechLabel style={{ color: `${DS.grid}77` }}>no records found</TechLabel>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <div>
          <ActivityList title="My Recruitment Activity" items={recruitmentActivity} />
        </div>
      </div>

      {/* ROW: Bench + Targets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div style={{ background: DS.paper, border: `1px solid ${DS.hairline}` }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: DS.hairline }}>
              <div className="flex items-center gap-3">
                <span className="w-2 h-2" style={{ background: DS.coral }} />
                <div>
                  <TechLabel style={{ color: DS.coral }}>03 · bench</TechLabel>
                  <h2 className="text-base font-bold leading-tight mt-0.5" style={{ color: DS.forest }}>
                    My bench activity
                  </h2>
                </div>
              </div>
              <TechLabel style={{ color: `${DS.grid}99` }}>{benchActivity.length} signals</TechLabel>
            </div>
            <Tabs defaultValue="activity" className="w-full">
              <div className="px-6 pt-4 border-b" style={{ borderColor: DS.hairline }}>
                <TabsList
                  className="bg-transparent rounded-none p-0 h-auto gap-px"
                  style={{ background: DS.hairline }}
                >
                  {[
                    { v: "activity", label: "Activity" },
                    { v: "interviews", label: "Interviews" },
                    { v: "applicants", label: "Applicants on bench" },
                  ].map((t) => (
                    <TabsTrigger
                      key={t.v}
                      value={t.v}
                      className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-[#1A3C2B] data-[state=active]:text-[#F7F7F5] data-[state=active]:shadow-none"
                      style={{ letterSpacing: "0.16em", background: DS.paper, color: DS.grid }}
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <TabsContent value="activity" className="p-6">
                <ul className="space-y-px" style={{ background: DS.hairline }}>
                  {benchActivity.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between py-2.5 px-3 transition-colors duration-150 hover:bg-white"
                      style={{ background: DS.paper }}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="w-1.5 h-1.5"
                          style={{ background: item.color === 'red' ? DS.coral : ACCENT }}
                        />
                        <span className="text-sm" style={{ color: DS.grid }}>{item.label}</span>
                      </div>
                      <span className="text-sm font-extrabold tabular-nums" style={{ color: DS.forest }}>
                        {item.value}
                      </span>
                    </li>
                  ))}
                </ul>
              </TabsContent>
              <TabsContent value="interviews" className="p-6">
                <div className="text-center py-8">
                  <TechLabel style={{ color: `${DS.grid}77` }}>no interviews scheduled</TechLabel>
                </div>
              </TabsContent>
              <TabsContent value="applicants" className="p-6">
                <div className="text-center py-8">
                  <TechLabel style={{ color: `${DS.grid}77` }}>no applicants on bench</TechLabel>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Bench approval */}
          <div style={{ background: DS.paper, border: `1px solid ${DS.hairline}` }}>
            <Tabs defaultValue="approval" className="w-full">
              <div className="px-6 pt-5 border-b" style={{ borderColor: DS.hairline }}>
                <TabsList
                  className="bg-transparent rounded-none p-0 h-auto gap-px"
                  style={{ background: DS.hairline }}
                >
                  {[
                    { v: "approval", label: "Bench approval" },
                    { v: "requisitions", label: "Pending requisitions" },
                  ].map((t) => (
                    <TabsTrigger
                      key={t.v}
                      value={t.v}
                      className="rounded-none px-4 py-2 text-xs font-bold uppercase data-[state=active]:bg-[#1A3C2B] data-[state=active]:text-[#F7F7F5] data-[state=active]:shadow-none"
                      style={{ letterSpacing: "0.16em", background: DS.paper, color: DS.grid }}
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <div className="mt-3 mb-4">
                  <TechLabel style={{ color: `${DS.grid}99` }}>04 · gating queue</TechLabel>
                </div>
              </div>
              <TabsContent value="approval" className="p-6">
                <div className="text-center py-8">
                  <TechLabel style={{ color: `${DS.grid}77` }}>no pending approvals</TechLabel>
                </div>
              </TabsContent>
              <TabsContent value="requisitions" className="p-6">
                <div className="text-center py-8">
                  <TechLabel style={{ color: `${DS.grid}77` }}>no pending requisitions</TechLabel>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* TARGETS */}
        <div>
          <TargetWidget targets={targets} />
        </div>
      </div>
    </div>
  );
}
